import { Injectable } from '@angular/core';

import { FirestorePathsService } from './firestorePaths.service';
import { UtilsService } from './utils.service';
import { DateService } from './date.service';
import { FirestoreService } from '../lib/firestore.service';
import { UserAccountMapsService } from '../lib/userAccountMaps.service';
import type { TAccountProfile, TAccountStatus } from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
/**
 * @description AccountProfileService provides methods to interact with account profiles stored in Firestore.
 * This service provides methods to interact with account profiles stored in Firestore.
 * It includes methods to get, set, and update account profiles, as well as retrieve profiles by account number or account ID.
 */
export class AccountProfileService {
    private documentName = 'accountProfile';

    constructor(private firestoreService: FirestoreService, private firestorePathsService: FirestorePathsService,
        private userAccountMapsService: UserAccountMapsService, private dateService: DateService,
        private utilsService: UtilsService) {
    }

    /**
     * @description getNullable returns an empty account profile object.
     * @return an empty account profile object.
     */
    public getNullable(): TAccountProfile {
        const profile: TAccountProfile = {
            accountNumber: '',
            activationInfo: {
                activationDate: this.dateService.getMinimumDate(),
            },
            ambassadorInfo: {},
            creditInfo: {
                availableCredits: 0
            },
            debug: false,
            featuresInfo: {},
            fromSmsNumber: '',
            notes: '',
            // packageName: '',
            periodInfo: {
                expirationDate: this.dateService.createTimestamp(),
                startDate: this.dateService.createTimestamp(),
                premiumSupportExpirationDate: this.dateService.createTimestamp(),
                webHostingExpirationDate: this.dateService.createTimestamp()
            },
            phoneNumber: '',
            pricingInfo: {},
            sku: '',
            // status: '',
            // type: '',
            uid: '',
            packageName: 'Trial Package',
            status: 'Active',
            signupDate: this.dateService.getMinimumDate()
        };
        return profile;
    }

    /**
     * @description getObservable returns an observable of account profiles.
     * @return an observable of account profiles.
     */
    // private getAccountProfileReference() {
    //     const collection = this.firestorePathsService.getProfilesCol();
    //     const accountProfileDoc = collection.doc(this.documentName);
    //     // this.accountProfileDoc = this.accountNumberDoc.collection('accountProfiles').doc('accountProfile');
    //     const ref = accountProfileDoc.ref;
    //     return ref;
    // }

    public async get(): Promise<TAccountProfile> {
        const path = this.firestorePathsService.getAccountProfilePath();
        const profile = await this.firestoreService.get(path) as TAccountProfile;
        return profile;
    }

    // public getByUid(uid: string): Promise<TAccountProfile> {
    //     const collection = this.firestorePathsService.getAccountProfilesCol();
    //     return this.firestoreService.getByUid(collection, uid);
    // }

    // public getByFieldValue(fieldName: string, fieldValue: string): Promise<TAccountProfile[]> {
    //     const collection = this.firestorePathsService.getAccountProfilesCol();
    //     return this.firestoreService.getByFieldValue(collection, fieldName, fieldValue);
    // }

    public async getAccountProfileByAccountNumber(accountNumber: string): Promise<TAccountProfile> {
        const path = this.firestorePathsService.getAccountProfilePathByAccountNumber(accountNumber);
        return this.firestoreService.get(path) as Promise<TAccountProfile>;
    }

    // public getByFieldValue(fieldName: string, fieldValue: string): Promise<TAccountProfile[]> {
    //     const collection = this.firestorePathsService.getAccountProfilesCol();
    //     return this.firestoreService.getByFieldValue(collection, fieldName, fieldValue);
    // }

    public async set(accountProfile: TAccountProfile): Promise<TAccountProfile> {
        accountProfile.uid = this.documentName;
        const collection = this.firestorePathsService.getProfilesPath();
        return this.firestoreService.set(collection, accountProfile) as Promise<TAccountProfile>;
    }

    public async update(accountProfile: TAccountProfile): Promise<TAccountProfile> {
        const collection = this.firestorePathsService.getProfilesPath();
        return this.firestoreService.update(collection, accountProfile) as Promise<TAccountProfile>;
    }

    //     public computeFeatureDesc(profile: TAccountProfile): TAccountProfile {
    // return;
    //         if (!profile) {
    //             return null;
    //         }

    //         let desc: string;
    //         desc  = 'Efficiency Package: ';
    //         desc += profile.featuresInfo.efficiency ? 'Enabled.' : 'Disabled.';
    //         // profile.efficiencyDesc = desc;

    //         desc  = 'Customer Communication Package: ';
    //         desc += profile.featuresInfo.customer ? 'Enabled.' : 'Disabled.';
    //         // profile.customerDesc = desc;

    //         desc  = 'Campaign Package: ';
    //         desc += profile.featuresInfo.campaign ? 'Enabled.' : 'Disabled.';
    //         // profile.campaignDesc = desc;

    //         return profile;
    //     }


    // Change the accountProfile.status depending on whether they paid and the current date.
    // If something changes then send a message to the Help | Message Center.
    // Called from loaderService.
    public async recalculateStatus(accountNumber: string): Promise<void> {
        const accountProfile = await this.getAccountProfileByAccountNumber(accountNumber);
        const expirationDate = accountProfile.periodInfo.expirationDate;
        const isExpired = this.dateService.isBeforeNow(expirationDate);
        const isTrialPackage = (accountProfile.packageName === 'Trial Package');
        const currentStatus: TAccountStatus = accountProfile.status;
        let newStatus: TAccountStatus | null = null;

        try {
            switch (currentStatus) {
                case 'Active':
                    if (isExpired) {
                        newStatus = isTrialPackage ? 'Trial Period Expired' : 'Package Expired';
                    }
                    break;

                case 'In Trial Period':
                    if (isExpired) {
                        newStatus = 'Trial Period Expired';
                    }
                    break;
    
                case 'Trial Period Expired':
                    if (!isExpired) {
                        newStatus = 'In Trial Period';
                    }
                    break;
                
                case 'Cancelled By Support':
                case 'Cancelled By Customer':
                    // Logic for cancelled by support status
                    // Logic for cancelled by customer status
                    // If the account has already been cancelled, for whatever reason, 
                    break;
    
                case 'Package Expired':
                    if (!isExpired) {
                        newStatus = isTrialPackage ? 'In Trial Period' : 'Active';
                    }
                    break;
            }
    
            if (newStatus && currentStatus !== newStatus) {
                accountProfile.status = newStatus;
                await this.update(accountProfile);
                
                // Send a message to the Help | Message Center
            }
        } catch (err) {
            // console.error('❌ Error recalculating account status:', err);
        }
    }
}

